import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Shield, CheckCircle2, ArrowRight, Target, Users } from 'lucide-react';

const Pricing = () => {
  const features = [
    'Unlimited client onboarding (free)',
    'Secure document collection portal',
    'AI-powered credit report analysis',
    'FCRA-compliant dispute letter generation',
    'PDF export & mail-ready disputes',
    'Round-by-round dispute tracking',
    '3-bureau progress dashboards',
    'Client status & activity feed',
    'CROA-compliant safeguards',
    'Email & priority chat support',
    'Cancel anytime — no contract',
    'Auto-prorated billing as you scale',
  ];

  return (
    <div className="min-h-screen bg-[#f7f6f0] text-stone-900">
      <nav className="border-b border-stone-200/80 bg-[#f7f6f0]/90 backdrop-blur sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5">
            <div className="w-9 h-9 bg-[#4b5320] flex items-center justify-center rounded-sm">
              <Shield className="w-5 h-5 text-white" strokeWidth={2.5} />
            </div>
            <div className="font-display text-lg tracking-wider">
              INFANTRY <span className="text-[#4b5320]">/ DISPUTE</span>
            </div>
          </Link>
          <div className="flex items-center gap-3">
            <Link to="/login" className="text-sm text-stone-700 hover:text-stone-900">Sign in</Link>
            <Link to="/signup">
              <Button className="bg-[#4b5320] hover:bg-[#3d4419] text-white font-medium rounded-sm">Get Started</Button>
            </Link>
          </div>
        </div>
      </nav>

      <section className="relative overflow-hidden py-20">
        <div className="absolute inset-0 diagonal-stripes opacity-50"></div>
        <div className="max-w-7xl mx-auto px-6 relative text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 border border-[#4b5320]/40 bg-[#4b5320]/10 rounded-sm mb-6">
            <Target className="w-3.5 h-3.5 text-[#4b5320]" />
            <span className="text-xs uppercase tracking-widest text-[#4b5320] font-medium">Pricing</span>
          </div>
          <h1 className="font-display text-5xl md:text-6xl tracking-tight mb-5">
            Pay only for the clients <span className="text-[#4b5320]">you're disputing for.</span>
          </h1>
          <p className="text-stone-700 max-w-2xl mx-auto">No setup fees. No tiers. Register free, add clients free — start paying $6.99/mo per client only when their first dispute is sent.</p>
        </div>
      </section>

      <section className="pb-24">
        <div className="max-w-4xl mx-auto px-6">
          <div className="relative border border-[#4b5320]/40 bg-white rounded-sm overflow-hidden shadow-sm">
            <div className="absolute top-0 right-0 bg-[#4b5320] text-white text-xs uppercase tracking-widest px-4 py-1.5 font-semibold">
              Usage-based
            </div>
            <div className="diagonal-stripes absolute inset-0 opacity-40 pointer-events-none"></div>
            <div className="relative p-10 md:p-12">
              <div className="grid md:grid-cols-2 gap-12 items-start">
                <div>
                  <div className="text-xs uppercase tracking-widest text-[#4b5320] mb-3">Per-Client Subscription</div>
                  <h2 className="font-display text-4xl mb-2">$6.99</h2>
                  <div className="text-stone-700 text-lg mb-6">per month, per active client</div>

                  <div className="border-l-2 border-[#4b5320] pl-4 mb-6">
                    <div className="text-sm text-stone-700">
                      Example: 10 clients in active dispute<br/>
                      <span className="font-mono text-stone-900">10 × $6.99 = $69.90/mo</span>
                    </div>
                  </div>

                  <div className="text-sm text-stone-700 mb-8 space-y-2">
                    <div className="flex items-start gap-2">
                      <Users className="w-4 h-4 text-[#4b5320] mt-0.5 flex-shrink-0" />
                      <span><strong>Free</strong> to register and onboard clients.</span>
                    </div>
                    <div className="flex items-start gap-2">
                      <CheckCircle2 className="w-4 h-4 text-[#4b5320] mt-0.5 flex-shrink-0" />
                      <span>You're only charged when a dispute is generated for that client.</span>
                    </div>
                    <div className="flex items-start gap-2">
                      <CheckCircle2 className="w-4 h-4 text-[#4b5320] mt-0.5 flex-shrink-0" />
                      <span>Subscription auto-scales as you add or pause clients. Prorated.</span>
                    </div>
                  </div>

                  <Link to="/signup">
                    <Button size="lg" className="w-full bg-[#4b5320] hover:bg-[#3d4419] text-white font-semibold rounded-sm h-12">
                      Register Free <ArrowRight className="ml-2 w-4 h-4" />
                    </Button>
                  </Link>
                  <div className="text-xs text-stone-600 text-center mt-3">No card required to sign up</div>
                </div>

                <div>
                  <div className="text-xs uppercase tracking-widest text-stone-500 mb-4">What's included</div>
                  <ul className="space-y-3">
                    {features.map((f, i) => (
                      <li key={i} className="flex items-start gap-3">
                        <CheckCircle2 className="w-4 h-4 text-[#4b5320] mt-0.5 flex-shrink-0" />
                        <span className="text-sm text-stone-800">{f}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-12 text-center">
            <div className="inline-block border-l-2 border-[#4b5320] pl-4 text-left">
              <div className="font-display text-xl text-stone-900">90-Day Performance Guarantee</div>
              <div className="text-sm text-stone-700 mt-1">If a client doesn't have at least one item removed within 90 days, we'll refund their first month.</div>
            </div>
          </div>
        </div>
      </section>

      <footer className="border-t border-stone-200/80 py-10">
        <div className="max-w-7xl mx-auto px-6 text-sm text-stone-600 text-center">
          &copy; {new Date().getFullYear()} Infantry Dispute. Built for credit repair professionals.
        </div>
      </footer>
    </div>
  );
};

export default Pricing;
